import { useMemo } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  ListSubheader,
  Box,
  Chip,
  Typography,
} from '@mui/material'

/**
 * Hierarchical Select Component for Clinic Brand > Location > Room
 * Displays rooms in a structured format: "Clinic Brand > Location > Room"
 */
export default function ClinicRoomHierarchicalSelect({
  rooms = [],
  selectedRoomIds = [],
  onChange,
  label = 'Clinic & Room',
  disabled = false,
  multiple = true,
}) {
  // Organize rooms hierarchically: Brand > Location > Rooms
  const hierarchicalStructure = useMemo(() => {
    const structure = new Map()

    rooms.forEach((room) => {
      const brandId = room.clinicBrand?.id || room.clinicBrandId
      const brandName = room.clinicBrand?.name || room.clinicBrand?.title || room.clinicBrandName || 'Unknown Brand'
      const locationId = room.clinicLocation?.id || room.clinicLocationId
      const locationName = room.clinicLocation?.name || room.clinicLocation?.title || room.clinicLocationName || 'Unknown Location'
      const roomId = room.id
      const roomTitle = room.title || `Room ${roomId}`

      if (!brandId || !locationId || !roomId) return

      // Initialize brand if not exists
      if (!structure.has(brandId)) {
        structure.set(brandId, {
          id: brandId,
          name: brandName,
          locations: new Map(),
        })
      }

      const brand = structure.get(brandId)

      // Initialize location if not exists
      if (!brand.locations.has(locationId)) {
        brand.locations.set(locationId, {
          id: locationId,
          name: locationName,
          rooms: [],
        })
      }

      // Add room to location
      brand.locations.get(locationId).rooms.push({
        id: roomId,
        title: roomTitle,
        fullPath: `${brandName} > ${locationName} > ${roomTitle}`,
      })
    })

    return structure
  }, [rooms])

  // Flatten structure for rendering
  const menuItems = useMemo(() => {
    const items = []
    const brands = Array.from(hierarchicalStructure.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    )

    brands.forEach((brand) => {
      // Add brand header
      items.push({
        type: 'brand-header',
        id: `brand-${brand.id}`,
        label: brand.name,
        brandId: brand.id,
      })

      const locations = Array.from(brand.locations.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      )

      locations.forEach((location) => {
        // Add location subheader
        items.push({
          type: 'location-subheader',
          id: `location-${location.id}`,
          label: location.name,
          locationId: location.id,
          brandId: brand.id,
        })

        // Add rooms under location
        location.rooms
          .sort((a, b) => a.title.localeCompare(b.title))
          .forEach((room) => {
            items.push({
              type: 'room',
              id: room.id,
              label: room.title,
              fullPath: room.fullPath,
              roomId: room.id,
              locationId: location.id,
              brandId: brand.id,
            })
          })
      })
    })

    return items
  }, [hierarchicalStructure])

  const handleChange = (event) => {
    const value = event.target.value
    const selected = typeof value === 'string' ? value.split(',') : value
    onChange(selected)
  }

  return (
    <FormControl sx={{ minWidth: 300 }} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={selectedRoomIds}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((roomId) => {
              const room = rooms.find((r) => r.id === roomId)
              if (!room) return <Chip key={roomId} label={roomId} size="small" />
              
              const brandName = room.clinicBrand?.name || room.clinicBrand?.title || room.clinicBrandName || 'Unknown'
              const locationName = room.clinicLocation?.name || room.clinicLocation?.title || room.clinicLocationName || 'Unknown'
              const roomTitle = room.title || `Room ${roomId}`
              
              return (
                <Chip
                  key={roomId}
                  label={`${brandName} > ${locationName} > ${roomTitle}`}
                  size="small"
                />
              )
            })}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
      >
        {menuItems.map((item) => {
          if (item.type === 'brand-header') {
            return (
              <ListSubheader key={item.id} sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
                {item.label}
              </ListSubheader>
            )
          }
          if (item.type === 'location-subheader') {
            return (
              <ListSubheader
                key={item.id}
                sx={{
                  pl: 3,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  backgroundColor: 'action.hover',
                }}
              >
                └ {item.label}
              </ListSubheader>
            )
          }
          // Room item
          return (
            <MenuItem key={item.id} value={item.roomId} sx={{ pl: 5 }}>
              <Typography variant="body2">{item.label}</Typography>
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}
